import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import HeaderFour from "../../components/Header/HeaderFour";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;

/* ─── Injected styles ──────────────────────────────────────────────── */
const STYLE_ID = "our-doctors-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes odFadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes odShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes odPulse {
      0%,100% { opacity: 1; }
      50% { opacity: .5; }
    }

    .od-hero {
      background: linear-gradient(135deg, #051c3c 0%, #0b3872 45%, #1558a8 100%);
      padding: 72px 28px 80px;
      text-align: center;
      position: relative;
      overflow: hidden;
    }
    .od-hero::before {
      content: '';
      position: absolute; inset: 0;
      background: radial-gradient(ellipse 80% 60% at 50% 120%, rgba(21,88,168,0.45) 0%, transparent 70%);
      pointer-events: none;
    }
    .od-hero__badge {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.22);
      backdrop-filter: blur(6px);
      border-radius: 50px; padding: 7px 22px;
      font-size: 11.5px; font-weight: 700; letter-spacing: 2px;
      text-transform: uppercase; color: #a8c8f8;
      margin-bottom: 20px;
    }
    .od-hero__title {
      font-size: clamp(26px, 4vw, 46px);
      font-weight: 900; color: #fff; margin: 0 0 14px;
      line-height: 1.15;
    }
    .od-hero__sub {
      font-size: 16px; color: rgba(255,255,255,0.65);
      max-width: 520px; margin: 0 auto 36px; line-height: 1.6;
    }
    .od-hero__stats {
      display: inline-flex; gap: 40px; flex-wrap: wrap; justify-content: center;
      background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14);
      border-radius: 16px; padding: 18px 36px;
    }
    .od-hero__stat-num {
      font-size: 28px; font-weight: 900; color: #fff; line-height: 1;
    }
    .od-hero__stat-lbl {
      font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.55);
      text-transform: uppercase; letter-spacing: 1px; margin-top: 4px;
    }

    .od-filters {
      background: #fff;
      border-bottom: 1px solid #e8f0fb;
      position: sticky; top: 0; z-index: 50;
      box-shadow: 0 4px 20px rgba(10,40,100,0.07);
    }
    .od-filters__inner {
      max-width: 1240px; margin: 0 auto;
      padding: 18px 28px;
      display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    }
    .od-filters__search-wrap {
      flex: 1 1 280px; position: relative;
    }
    .od-filters__search-icon {
      position: absolute; left: 15px; top: 50%; transform: translateY(-50%);
      color: #94a3b8; font-size: 14px; pointer-events: none;
    }
    .od-filters__search {
      width: 100%; padding: 12px 16px 12px 40px;
      border: 1.5px solid #e2e8f0; border-radius: 10px;
      font-size: 14px; color: #1e293b; outline: none;
      transition: border-color .2s;
    }
    .od-filters__search:focus { border-color: #1558a8; }
    .od-filters__select {
      padding: 12px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px;
      font-size: 14px; color: #1e293b; background: #fff;
      outline: none; cursor: pointer; flex: 0 1 220px; min-width: 180px;
      transition: border-color .2s;
    }
    .od-filters__select:focus { border-color: #1558a8; }
    .od-filters__toggle-group {
      display: flex; gap: 6px;
    }
    .od-filters__toggle {
      padding: 10px 18px; border-radius: 8px;
      border: 1.5px solid #e2e8f0; background: #f8fafc;
      font-size: 12.5px; font-weight: 700; color: #64748b;
      cursor: pointer; transition: all .2s; white-space: nowrap;
    }
    .od-filters__toggle.active {
      background: #1558a8; border-color: #1558a8; color: #fff;
    }
    .od-filters__count {
      font-size: 13px; color: #64748b; white-space: nowrap; margin-left: auto;
    }
    .od-filters__count strong { color: #1558a8; }

    .od-body {
      background: #f0f5fc;
      padding: 48px 28px 80px;
      min-height: 400px;
    }
    .od-grid {
      max-width: 1240px; margin: 0 auto;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 22px;
    }
    @media (max-width: 1100px) { .od-grid { grid-template-columns: repeat(3, 1fr); } }
    @media (max-width: 768px)  { .od-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 480px)  { .od-grid { grid-template-columns: 1fr; } }

    /* ── Doctor Card ── */
    .od-card {
      background: #fff; border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 4px 18px rgba(10,40,100,0.07);
      border: 1px solid rgba(10,40,100,0.06);
      display: flex; flex-direction: column;
      transition: transform .25s ease, box-shadow .25s ease;
      animation: odFadeUp .4s ease both;
    }
    .od-card:hover {
      transform: translateY(-7px);
      box-shadow: 0 18px 40px rgba(10,40,100,0.14);
    }
    .od-card__photo {
      position: relative;
      height: 200px;
      background: linear-gradient(150deg, #0b3872 0%, #1558a8 60%, #2176d2 100%);
      overflow: hidden;
      display: flex; align-items: flex-end; justify-content: center;
    }
    .od-card__img {
      width: 100%; height: 100%; object-fit: cover; object-position: top center;
      display: block;
    }
    .od-card__avatar {
      width: 90px; height: 90px;
      border-radius: 50%; border: 3px solid rgba(255,255,255,0.3);
      background: rgba(255,255,255,0.1);
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 22px;
      font-size: 36px; font-weight: 900; color: rgba(255,255,255,0.8);
    }
    .od-card__badges {
      position: absolute; top: 12px; right: 12px;
      display: flex; flex-direction: column; gap: 5px; align-items: flex-end;
    }
    .od-card__badge {
      font-size: 9.5px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase;
      padding: 3px 9px; border-radius: 50px;
    }
    .od-card__badge--opd { background: rgba(34,197,94,0.9); color: #fff; }
    .od-card__badge--ipd { background: rgba(168,85,247,0.9); color: #fff; }

    .od-card__body {
      padding: 18px 20px 14px;
      flex: 1; display: flex; flex-direction: column;
    }
    .od-card__specialty {
      font-size: 11px; font-weight: 800; letter-spacing: 1.5px;
      text-transform: uppercase; color: #1558a8;
      margin-bottom: 6px;
      display: flex; align-items: center; gap: 5px;
    }
    .od-card__name {
      font-size: 1.05rem; font-weight: 800; color: #0b1f3a;
      margin: 0 0 4px; line-height: 1.3;
    }
    .od-card__designation {
      font-size: 12px; color: #64748b; margin-bottom: 6px;
      font-weight: 500;
    }
    .od-card__edu {
      font-size: 11.5px; color: #94a3b8;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      margin-bottom: 10px;
    }
    .od-card__clinic {
      display: inline-flex; align-items: center; gap: 5px;
      font-size: 11px; color: #1558a8; font-weight: 600;
      background: #eef4fd; border-radius: 6px; padding: 3px 10px;
      margin-bottom: 14px; width: fit-content;
    }
    .od-card__foot {
      display: flex; gap: 8px; margin-top: auto; padding-top: 14px;
      border-top: 1px solid #f1f5f9;
    }
    .od-card__btn-profile {
      flex: 1; text-align: center; padding: 10px;
      border-radius: 9px; border: 1.5px solid #e2e8f0;
      background: #f8fafc; color: #1e293b;
      font-weight: 700; font-size: 12.5px;
      text-decoration: none; transition: all .2s;
    }
    .od-card__btn-profile:hover { border-color: #1558a8; color: #1558a8; background: #eef4fd; }
    .od-card__btn-book {
      flex: 1.4; text-align: center; padding: 10px;
      border-radius: 9px;
      background: linear-gradient(135deg, #1558a8 0%, #1e88e5 100%);
      color: #fff; font-weight: 700; font-size: 12.5px;
      text-decoration: none; transition: all .2s;
      display: flex; align-items: center; justify-content: center; gap: 6px;
    }
    .od-card__btn-book:hover { background: linear-gradient(135deg, #0d3b6e 0%, #1558a8 100%); color: #fff; box-shadow: 0 6px 18px rgba(21,88,168,0.35); }

    /* ── Skeleton ── */
    .od-skel {
      background: #fff; border-radius: 18px; overflow: hidden;
      border: 1px solid rgba(10,40,100,0.06);
      box-shadow: 0 4px 18px rgba(10,40,100,0.05);
    }
    .od-skel__photo { height: 200px; }
    .od-skel__body { padding: 18px 20px; }
    .od-shimmer {
      border-radius: 6px;
      background: linear-gradient(90deg, #e2e8f0 25%, #f0f4fa 50%, #e2e8f0 75%);
      background-size: 600px 100%;
      animation: odShimmer 1.4s infinite;
    }

    /* ── Empty state ── */
    .od-empty {
      grid-column: 1 / -1; text-align: center; padding: 80px 24px;
      color: #94a3b8;
    }
    .od-empty__icon { font-size: 56px; margin-bottom: 16px; opacity: .4; }
    .od-empty__title { font-size: 20px; font-weight: 700; color: #475569; margin-bottom: 8px; }
  `;
  document.head.appendChild(s);
}

/* ─── Helpers ──────────────────────────────────────────────────────── */
function getInitials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

/* ─── Skeleton Card ─────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="od-skel">
      <div className="od-skel__photo od-shimmer" />
      <div className="od-skel__body">
        <div className="od-shimmer" style={{ height: 10, width: "50%", marginBottom: 10 }} />
        <div className="od-shimmer" style={{ height: 16, width: "80%", marginBottom: 8 }} />
        <div className="od-shimmer" style={{ height: 12, width: "65%", marginBottom: 8 }} />
        <div className="od-shimmer" style={{ height: 10, width: "75%", marginBottom: 18 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <div className="od-shimmer" style={{ flex: 1, height: 38, borderRadius: 9 }} />
          <div className="od-shimmer" style={{ flex: 1.4, height: 38, borderRadius: 9 }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Doctor Card ───────────────────────────────────────────────────── */
function DoctorCard({ doctor, animDelay }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const urlRef = useRef(null);
  const specialty = doctor.websiteSpeciality?.name || doctor.speciality || "";
  const clinic = doctor.clinic || "";

  useEffect(() => {
    let revokeUrl = null;

    // Case 1: image blob is already embedded in the list response (noPagination=1 path)
    // The backend returns a JSON-serialised Buffer: { type: "Buffer", data: [bytes…] }
    if (doctor.image) {
      try {
        const bytes = Array.isArray(doctor.image) ? doctor.image : doctor.image?.data ? doctor.image.data : null;
        if (bytes && bytes.length > 0) {
          const uint8 = new Uint8Array(bytes);
          const blob = new Blob([uint8]); // browser sniffs MIME from magic bytes
          const url = URL.createObjectURL(blob);
          revokeUrl = url;
          urlRef.current = url;
          setImgSrc(url);
          return () => URL.revokeObjectURL(url);
        }
      } catch {
        // fall through to endpoint fetch
      }
    }

    // Case 2: paginated endpoint only provides hasImage flag — fetch the image
    if (!doctor.hasImage) return;
    let cancelled = false;
    fetch(`${BASE}/website/doctor/${doctor.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setImgSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [doctor.id, doctor.hasImage, doctor.image]);

  return (
    <div className="od-card" style={{ animationDelay: `${animDelay}ms` }}>
      {/* Photo */}
      <div className="od-card__photo">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={doctor.name}
            className="od-card__img"
            onLoad={() => setImgLoaded(true)}
            style={{ opacity: imgLoaded ? 1 : 0, transition: "opacity .3s" }}
          />
        ) : (
          <div className="od-card__avatar">{getInitials(doctor.name)}</div>
        )}
        {/* OPD / IPD badges */}
        <div className="od-card__badges">
          {doctor.isOpd && <span className="od-card__badge od-card__badge--opd">OPD</span>}
          {doctor.isIpd && <span className="od-card__badge od-card__badge--ipd">IPD</span>}
        </div>
      </div>

      {/* Body */}
      <div className="od-card__body">
        {specialty && (
          <div className="od-card__specialty">
            <i className="fa-solid fa-stethoscope" style={{ fontSize: 10 }} />
            {specialty}
          </div>
        )}
        <h4 className="od-card__name">{doctor.name}</h4>
        {doctor.designation && <div className="od-card__designation">{doctor.designation}</div>}
        {doctor.educationTagLine && (
          <div className="od-card__edu" title={doctor.educationTagLine}>
            {doctor.educationTagLine}
          </div>
        )}
        {clinic && (
          <span className="od-card__clinic">
            <i className="fa-solid fa-building-columns" style={{ fontSize: 10 }} />
            {clinic}
          </span>
        )}

        {/* CTA */}
        <div className="od-card__foot">
          <Link to={`/doctor/${doctor.id}`} className="od-card__btn-profile">
            Profile
          </Link>
          <Link to="/appointment" className="od-card__btn-book">
            <i className="fa-solid fa-calendar-plus" style={{ fontSize: 12 }} />
            Consult Now
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────────────────── */
const OurDoctors = ({ asSection = false }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [specFilter, setSpecFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | "opd" | "ipd"

  useEffect(() => {
    makeRequest({ route: "/website/doctor?noPagination=1", method: "get" })
      .then(({ data }) => Array.isArray(data) && setDoctors(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const specialities = useMemo(() => {
    const set = new Set();
    doctors.forEach((d) => {
      const s = d.websiteSpeciality?.name || d.speciality;
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [doctors]);

  const filtered = useMemo(() => {
    return doctors
      .filter((d) => {
        if (typeFilter === "opd" && !d.isOpd) return false;
        if (typeFilter === "ipd" && !d.isIpd) return false;
        const s = d.websiteSpeciality?.name || d.speciality || "";
        if (specFilter && s !== specFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          const text = `${d.name} ${s} ${d.designation || ""} ${d.clinic || ""}`.toLowerCase();
          if (!text.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aOrder = a.sortOrder ?? Infinity;
        const bOrder = b.sortOrder ?? Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.id - b.id;
      });
  }, [doctors, search, specFilter, typeFilter]);

  return (
    <>
      {!asSection && (
        <Seo
          title="Our Doctors"
          description="Meet our team of expert doctors at Maroof International Hospital, Islamabad. Browse profiles, qualifications, and book appointments."
          path="/our-doctors"
        />
      )}
      {!asSection && <HeaderFour />}

      {/* ── Hero ── */}
      <div className="od-hero">
        <div className="od-hero__badge">
          <i className="fa-solid fa-user-doctor" />
          Our Medical Team
        </div>
        <h1 className="od-hero__title">Find the Right Doctor for You</h1>
        <p className="od-hero__sub">
          Our team of highly qualified specialists is dedicated to delivering exceptional, compassionate care at Maroof International Hospital.
        </p>
        {!loading && doctors.length > 0 && (
          <div className="od-hero__stats">
            <div>
              <div className="od-hero__stat-num">{doctors.length}+</div>
              <div className="od-hero__stat-lbl">Specialists</div>
            </div>
            <div>
              <div className="od-hero__stat-num">{specialities.length}+</div>
              <div className="od-hero__stat-lbl">Specialties</div>
            </div>
            <div>
              <div className="od-hero__stat-num">{doctors.filter((d) => d.isOpd).length}</div>
              <div className="od-hero__stat-lbl">OPD Doctors</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="od-filters">
        <div className="od-filters__inner">
          <div className="od-filters__search-wrap">
            <i className="fa-solid fa-magnifying-glass od-filters__search-icon" />
            <input
              type="search"
              className="od-filters__search"
              placeholder="Search by name, specialty, clinic…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="od-filters__select" value={specFilter} onChange={(e) => setSpecFilter(e.target.value)}>
            <option value="">All Specialties</option>
            {specialities.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <div className="od-filters__toggle-group">
            {[
              ["all", "All"],
              ["opd", "OPD"],
              ["ipd", "IPD"],
            ].map(([val, label]) => (
              <button key={val} className={`od-filters__toggle${typeFilter === val ? " active" : ""}`} onClick={() => setTypeFilter(val)}>
                {label}
              </button>
            ))}
          </div>
          {!loading && (
            <span className="od-filters__count">
              <strong>{filtered.length}</strong> doctor{filtered.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="od-body">
        <div className="od-grid">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          ) : filtered.length === 0 ? (
            <div className="od-empty">
              <div className="od-empty__icon">
                <i className="fa-solid fa-user-doctor" />
              </div>
              <div className="od-empty__title">No doctors found</div>
              <p>Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            filtered.map((d, i) => <DoctorCard key={d.id} doctor={d} animDelay={Math.min(i * 40, 320)} />)
          )}
        </div>
      </div>

      {!asSection && <Footer />}
    </>
  );
};

export default OurDoctors;
